import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { PalangDaftar, Paginasi } from "../../components/etams/PalangDaftar";
import { useDaftar } from "../../hooks/useDaftar";
import { useAsetMeta, label, tone, labelPengingat } from "../../hooks/useAsetMeta";
import { listJadwalJatuhTempo, apiErrorMessage } from "../../lib/asetApi";
import { ASET_NAV_ROLES } from "../../lib/roles";
import { formatDate } from "../../lib/format";
import { CalendarClock, RefreshCw, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import CatatServisDialog from "./CatatServisDialog";
import { useRegions, formatKm } from "./bantuAset";

// Jadwal Maintenance (L3b, 4b-2) — SEMUA jadwal AKTIF lintas aset dari view
// v_aset_jadwal_jatuh_tempo (GET /api/aset/jadwal; KC/ADMIN dijepit region
// profil, OWNER pilih region atau semua), urut paling mendesak dulu.
//
// Urutan: TINGKAT (TERLAMBAT = KM_BASI → H1 → H3 → H7 → MENDEKATI → NORMAL),
// lalu di dalam tingkat: tanggal jatuh tempo terdekat; baris KILOMETER (tanpa
// tanggal) di belakang baris bertanggal pada tingkat yang sama, diurut sisa km.
//
// Filter URL (tautan butir dashboard 4a, `aset_meta.TAUTAN_*`):
//   ?status=terlambat → hanya TERLAMBAT + KM_BASI
//   ?untuk=saya       → hanya peran_notifikasi = jabatan pengguna
// Keduanya bisa dilepas dari layar. Cari/paginasi di klien (`useDaftar`);
// urutan dikunci oleh layar (kolom nol bisa diurut ulang — mendesak dulu).
//
// L4–L6/S1: label + rona dari meta (L5 dengan n hari lewat `labelPengingat`).
// Tombol "Catat Servis" = ASET_NAV_ROLES (cermin PERAN_CATAT); backend tetap
// pagar utamanya (`wajib_catat` region aset).

const SEMUA = "__semua__";
const TINGKAT = { TERLAMBAT: 0, KM_BASI: 0, H1: 1, H3: 2, H7: 3, MENDEKATI: 4, NORMAL: 5 };
const TERLAMBAT_SET = new Set(["TERLAMBAT", "KM_BASI"]);

export function urutMendesak(a, b) {
  const ta = TINGKAT[a.status_pengingat] ?? 9;
  const tb = TINGKAT[b.status_pengingat] ?? 9;
  if (ta !== tb) return ta - tb;
  const da = a.tanggal_jatuh_tempo || null;
  const db = b.tanggal_jatuh_tempo || null;
  if (da && db) return da < db ? -1 : da > db ? 1 : 0;
  if (da) return -1;
  if (db) return 1;
  const sa = (a.km_jatuh_tempo ?? Infinity) - (a.km_terakhir ?? 0);
  const sb = (b.km_jatuh_tempo ?? Infinity) - (b.km_terakhir ?? 0);
  return sa - sb;
}

export default function JadwalMaintenance() {
  const { roleCode, isOwner } = useAuth();
  const canCatat = ASET_NAV_ROLES.includes(roleCode);
  const { meta } = useAsetMeta();
  const [params, setParams] = useSearchParams();
  const hanyaTerlambat = params.get("status") === "terlambat";
  const untukSaya = params.get("untuk") === "saya";

  const [items, setItems] = useState([]);
  const [memuat, setMemuat] = useState(true);
  const [regionFilter, setRegionFilter] = useState("");
  const [servis, setServis] = useState(null);   // {aset, jadwalId, hariIni}
  const regions = useRegions(isOwner);

  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const p = {};
      if (isOwner && regionFilter) p.region_id = regionFilter;
      const r = await listJadwalJatuhTempo(p);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat jadwal maintenance."));
      setItems([]);
    } finally {
      setMemuat(false);
    }
  }, [isOwner, regionFilter]);
  useEffect(() => { muat(); }, [muat]);

  const tersaring = useMemo(() => {
    let b = items;
    if (hanyaTerlambat) b = b.filter((j) => TERLAMBAT_SET.has(j.status_pengingat));
    if (untukSaya) b = b.filter((j) => j.peran_notifikasi === roleCode);
    return [...b].sort(urutMendesak);
  }, [items, hanyaTerlambat, untukSaya, roleCode]);

  const daftar = useDaftar(tersaring, {
    kunciCari: ["aset_kode", "aset_nama", "nama_jadwal"],
    urutAwal: null,
  });

  const lepas = (kunci) => { params.delete(kunci); setParams(params, { replace: true }); };
  const jumlahTerlambat = items.filter((j) => TERLAMBAT_SET.has(j.status_pengingat)).length;

  const bukaServis = (j) => setServis({
    aset: { id: j.aset_id, kode: j.aset_kode, nama: j.aset_nama, kategori: j.aset_kategori, region_id: j.region_id },
    jadwalId: j.jadwal_id,
    hariIni: j.tanggal_hari_ini_region || null,
  });

  const jatuhTempo = (j) => (j.bentuk_jadwal === "KILOMETER"
    ? `${formatKm(j.km_jatuh_tempo)}${j.km_terakhir != null ? ` (terbaca ${formatKm(j.km_terakhir)})` : ""}`
    : formatDate(j.tanggal_jatuh_tempo));

  return (
    <div className="space-y-6" data-testid="jadwal-maintenance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <CalendarClock className="h-6 w-6" /> Jadwal Maintenance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Semua jadwal aktif, yang paling mendesak di atas.
            {jumlahTerlambat > 0 ? ` ${jumlahTerlambat} jadwal terlambat.` : ""}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={muat} data-testid="jadwal-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {(hanyaTerlambat || untukSaya) && (
        <div className="flex flex-wrap gap-2" data-testid="jadwal-filter-url">
          {hanyaTerlambat && (
            <Button variant="outline" size="sm" onClick={() => lepas("status")} data-testid="jadwal-filter-terlambat">
              Hanya yang terlambat <X size={14} className="ml-1" />
            </Button>
          )}
          {untukSaya && (
            <Button variant="outline" size="sm" onClick={() => lepas("untuk")} data-testid="jadwal-filter-saya">
              Untuk jabatan saya ({label(meta, "peran_notifikasi", roleCode)}) <X size={14} className="ml-1" />
            </Button>
          )}
        </div>
      )}

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari kode / nama aset / nama jadwal…" testId="jadwal-palang">
          {isOwner && regions.length > 0 && (
            <Select value={regionFilter || SEMUA} onValueChange={(v) => setRegionFilter(v === SEMUA ? "" : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="jadwal-filter-region"><SelectValue placeholder="Wilayah" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SEMUA}>Semua Wilayah</SelectItem>
                {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </PalangDaftar>

        {memuat ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="jadwal-memuat">Memuat...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Aset</th>
                  <th className="text-left px-3 py-2">Jadwal</th>
                  <th className="text-left px-3 py-2">Bentuk</th>
                  <th className="text-left px-3 py-2">Jatuh Tempo</th>
                  <th className="text-left px-3 py-2">Pengingat</th>
                  <th className="text-left px-3 py-2">Pengingat ke</th>
                  {canCatat && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && (
                  <tr><td colSpan={canCatat ? 7 : 6} className="px-3 py-8 text-center text-muted-foreground" data-testid="jadwal-kosong">
                    {daftar.cari.trim()
                      ? "Tidak ada jadwal yang cocok."
                      : hanyaTerlambat
                        ? "Tidak ada jadwal yang terlambat."
                        : untukSaya
                          ? "Tidak ada jadwal untuk jabatan Anda."
                          : "Belum ada jadwal maintenance aktif. Jadwal dibuat dari halaman Detail Aset."}
                  </td></tr>
                )}
                {daftar.tampil.map((j) => (
                  <tr key={j.jadwal_id} className="border-t border-border hover:bg-muted/50" data-testid={`jadwal-row-${j.jadwal_id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/aset/${j.aset_id}`} className="font-mono text-xs underline underline-offset-2 hover:text-foreground">{j.aset_kode}</Link>
                      <span className="block">{j.aset_nama}</span>
                    </td>
                    <td className="px-3 py-2 font-medium">{j.nama_jadwal}</td>
                    <td className="px-3 py-2 text-xs">{label(meta, "bentuk_jadwal", j.bentuk_jadwal)}</td>
                    <td className="px-3 py-2 text-xs tabular-nums">{jatuhTempo(j)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={tone(meta, "status_pengingat", j.status_pengingat)} testId={`jadwal-pengingat-${j.jadwal_id}`}>
                        {labelPengingat(meta, j)}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2 text-xs">{label(meta, "peran_notifikasi", j.peran_notifikasi)}</td>
                    {canCatat && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => bukaServis(j)} data-testid={`jadwal-catat-${j.jadwal_id}`}>
                          <Wrench className="h-4 w-4 mr-1" /> Catat Servis
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="jadwal-paginasi" />
          </div>
        )}
      </Card>

      <CatatServisDialog
        terbuka={!!servis}
        onTutup={() => setServis(null)}
        onSelesai={() => { setServis(null); muat(); }}
        meta={meta}
        aset={servis?.aset || null}
        jadwalAktif={null}
        jadwalId={servis?.jadwalId || null}
        jenisBawaan="PREVENTIVE"
        hariIni={servis?.hariIni || null}
      />
    </div>
  );
}
